// ABOUTME: React hook that reads Plotly CSS variables from the DOM.
// ABOUTME: Returns a memoized layout override object that updates when theme changes.

import { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@/context/ThemeContext';

/**
 * Theme-aware layout overrides for Plotly charts.
 * This interface is a subset of Plotly.Layout with guaranteed properties.
 */
export interface PlotlyLayoutOverrides extends Partial<Plotly.Layout> {
  autosize: boolean;
  margin: { l: number; r: number; t: number; b: number };
  paper_bgcolor: string;
  plot_bgcolor: string;
  font: {
    family: string;
    color: string;
  };
  title: {
    font: { color: string };
  };
  xaxis: {
    tickfont: { color: string };
    gridcolor: string;
  };
  yaxis: {
    tickfont: { color: string };
    gridcolor: string;
  };
  legend: {
    font: { color: string };
  };
  coloraxis: {
    colorbar: {
      tickfont: { color: string };
      title: { font: { color: string } };
    };
  };
  geo: {
    bgcolor: string;
  };
}

/**
 * CSS variable names for Plotly theme colors.
 */
const CSS_VARS = {
  textPrimary: '--plotly-text-primary',
  textSecondary: '--plotly-text-secondary',
  bg: '--plotly-bg',
  grid: '--plotly-grid',
  paperBg: '--plotly-paper-bg',
} as const;

/**
 * Reads CSS variable values from the document root element.
 */
function readCssVariables(): Record<keyof typeof CSS_VARS, string> {
  const style = getComputedStyle(document.documentElement);
  return {
    textPrimary: style.getPropertyValue(CSS_VARS.textPrimary).trim(),
    textSecondary: style.getPropertyValue(CSS_VARS.textSecondary).trim(),
    bg: style.getPropertyValue(CSS_VARS.bg).trim(),
    grid: style.getPropertyValue(CSS_VARS.grid).trim(),
    paperBg: style.getPropertyValue(CSS_VARS.paperBg).trim(),
  };
}

/**
 * Hook that returns Plotly layout overrides based on CSS variables.
 *
 * Reads theme colors from CSS custom properties defined in globals.css.
 * Re-reads the variables when the theme changes (light/dark mode toggle).
 *
 * @returns Memoized PlotlyLayoutOverrides object
 */
export function usePlotlyTheme(): PlotlyLayoutOverrides {
  const { theme } = useTheme();

  // Track CSS variable values in state - read synchronously on init
  const [cssValues, setCssValues] = useState<Record<keyof typeof CSS_VARS, string>>(readCssVariables);

  // Re-read CSS variables when theme changes
  // Note: CSS variables update synchronously when class changes, no delay needed
  useEffect(() => {
    // Use requestAnimationFrame to batch with browser's paint cycle
    // This ensures CSS has been applied but avoids the race condition from setTimeout
    const frameId = requestAnimationFrame(() => {
      const newValues = readCssVariables();
      // Only update if values changed to preserve memoization
      setCssValues((prev) => {
        const keys: Array<keyof typeof CSS_VARS> = ['textPrimary', 'textSecondary', 'bg', 'grid', 'paperBg'];
        const changed = keys.some((k) => prev[k] !== newValues[k]);
        return changed ? newValues : prev;
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [theme]);

  // Memoize the layout overrides object
  const layoutOverrides = useMemo<PlotlyLayoutOverrides>(
    () => ({
      autosize: true,
      margin: { l: 40, r: 40, t: 40, b: 40 },
      paper_bgcolor: cssValues.paperBg,
      plot_bgcolor: cssValues.bg,
      font: {
        family: 'Geist, system-ui, -apple-system, sans-serif',
        color: cssValues.textPrimary,
      },
      title: {
        font: { color: cssValues.textPrimary },
      },
      xaxis: {
        tickfont: { color: cssValues.textSecondary },
        gridcolor: cssValues.grid,
      },
      yaxis: {
        tickfont: { color: cssValues.textSecondary },
        gridcolor: cssValues.grid,
      },
      legend: {
        font: { color: cssValues.textPrimary },
      },
      coloraxis: {
        colorbar: {
          tickfont: { color: cssValues.textSecondary },
          title: { font: { color: cssValues.textPrimary } },
        },
      },
      geo: {
        bgcolor: cssValues.paperBg,
      },
    }),
    [cssValues]
  );

  return layoutOverrides;
}
